import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { SubSink } from 'subsink';
import { PacimAuthorizationType } from '@core/enumeration';
import { ClinicalInformationPacimService } from '@core/service';
import { FormBaseData, FormioFormConfig } from '@mynexus/mynexus-ui-lib';
import { calculateAge } from '@shared/utility';

@Component({
    selector: '<clinical-information-tab>',
    templateUrl: './clinical-information-tab.component.html',
    styleUrls: ['./clinical-information-tab.component.scss'],
    standalone: false
})
export class ClinicalInformationTabComponent implements OnInit {
  @Input() authorizationType!: string;
  @Input() isInternalFlow!: boolean;
  @Input() tenantId: string;
  @Input() productShortName: string;
  @Input() admitDate: string;
  @Input() authorizationNumber: string;
  @Input() clinicalInformation: FormBaseData;
  @Input() clinicalInformationId: {[key: string]: string};
  @Input() savingAuthorization: boolean;
  @Input() tenantName: string;
  @Input() subscriberNumber: string;
  @Input() memberState: string;
  @Input() dateOfBirth: string;
  @Input() lineOfBusiness: string;

  @Output() backStep = new EventEmitter<void>();
  @Output() cancelProcess = new EventEmitter<void>();
  @Output() submitStep = new EventEmitter<any | void>();
  @Output() update = new EventEmitter<any | void>();

  formConfig: FormioFormConfig;
  formData: FormBaseData = null;
  submission: FormBaseData = null;
  isFormValid = false;

  get isNextStepEnabled() {
    return this.isFormValid && !this.savingAuthorization;
  }

  private readonly subs = new SubSink();

  constructor(
    private readonly clinicalInfoService: ClinicalInformationPacimService,
    private readonly confirmationService: ConfirmationService,
    private readonly ref: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.submission = {
      data: {
        ...(this.clinicalInformation[this.authorizationType]?.form.submission || {}),
        productShortName: this.productShortName,
        admitDate: this.admitDate,
        subscriberID: this.subscriberNumber,
        tenant: this.tenantName,
        state: this.memberState,
        age: calculateAge(this.dateOfBirth),
        lineOfBusiness: this.lineOfBusiness,
        ...(this.memberState === 'TX' &&
          this.tenantName === 'Wellpoint' &&
          this.authorizationType === 'SNF' && {
          healthPlan: this.tenantName,
          memberState: this.memberState,
        }),
      },
    };

    this.fetchForm();

    if (this.shouldShowBypass()) {
      this.bypass();
    }
  }

  onCancel(): void {
    this.cancelProcess.emit();
  }

  onClickBack(): void {
    this.backStep.emit();
  }

  onSubmit(): void {
    this.submitStep.emit({
      form: {
        id: this.formConfig._id,
        revision: this.formConfig._vid,
        submission: this.formData,
      },
    });
  }

  onChange(changes: any): void {
    if (changes.detail?.data) {
      // formio throws 2 change events on initial render with false positive validity
      // the only correct validity comes if we have changed in event data
      if (changes.detail.changed?.component.validate.required) {
        this.isFormValid = changes.detail.isValid;
      } else if (this.formData && changes.detail.isValid !== undefined && changes.detail.isModified) {
        this.isFormValid = changes.detail.isValid;
      }
      this.formData = changes.detail?.data;
      this.update.emit({
        form: {
          id: this.formConfig._id,
          revision: this.formConfig._vid,
          submission: this.formData,
        },
      });
    }
  }

  private bypass(): void {
    this.confirmationService.confirm({
      reject: () => this.submitStep.emit()
    });
  }

  private fetchForm(): void {
    this.subs.sink = this.clinicalInfoService
      .fetchLatestRevisionForm(
        this.tenantId,
        this.authorizationType as PacimAuthorizationType
      )
      .subscribe((res) => {
        const submitButton = res.components.find((c) => c.key === 'submit');

        if (submitButton) {
          submitButton.hidden = true;
        }

        this.formConfig = res;
        this.ref.markForCheck();
      });
  }

  private shouldShowBypass(): boolean {
    return this.isInternalFlow && !this.clinicalInformationId[this.authorizationType];
  }
}
